/*
  Stage 4 optional backend integration client.
  Server save is an enhancement only: when REACT_APP_BACKEND_URL is absent the
  app remains fully localStorage-driven. Only de-identified, provider-level,
  bounded snapshot data is ever sent (no consultant notes, no ABN, no
  free-text answers other than the provider organisation name). The backend
  applies its own PII guardrail and may reject with PII_GUARDRAIL_TRIGGERED.
*/
import { getProviderSnapshot } from "@/lib/assessmentLogic";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function isServerSaveAvailable() {
  return Boolean(BACKEND_URL);
}

export function buildIntakePayload(answers, result) {
  const snapshot = getProviderSnapshot(answers, result);
  const reasons = result.manualReviewReasons || [];
  return {
    providerName: String(answers.provider_name || "").trim(),
    registrationStatus: answers.profile_registration_status || null,
    deliversSIL: answers.sil_currently_delivering === "yes",
    currentSupportTypes: snapshot.providerType === "Not indicated" ? [] : [snapshot.providerType],
    providerCategory: result.category.id,
    readinessLevel: result.classifier.pathway.id,
    evidenceStatus: Object.fromEntries((result.evidenceItems || []).map((i) => [i.id, i.rag])),
    operationalRiskFlags: (result.operationalRisks || [])
      .filter((r) => r.level === "high" || r.level === "medium")
      .map((r) => `${r.id}:${r.level}`),
    safeguardingProcessFlags: reasons,
    manualReviewRequired: Boolean(result.manualReview),
    manualReviewReason: result.manualReview ? reasons.join("; ").slice(0, 1000) || null : null,
    sourceStatus: String(
      result.classifier.sourceVerification.status || "requires_source_verification"
    ).toUpperCase()
  };
}

export async function saveIntakeToServer(payload) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/intakes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (response.status === 201) {
      return { ok: true, intake: await response.json() };
    }
    let detail = null;
    try {
      detail = (await response.json()).detail;
    } catch (parseError) {
      detail = null;
    }
    if (response.status === 422 && detail && detail.error === "PII_GUARDRAIL_TRIGGERED") {
      return { ok: false, kind: "pii_guardrail" };
    }
    if (process.env.NODE_ENV !== "production") {
      // Safe metadata only — never response bodies or payload content.
      console.warn("backendClient: server save rejected", { status: response.status });
    }
    return { ok: false, kind: response.status === 422 ? "validation" : "server" };
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("backendClient: server unreachable", { name: error && error.name });
    }
    return { ok: false, kind: "network" };
  }
}
